import type { WeekdayCode } from './passPlan';
import type { ReviewStatus } from './review';

/**
 * Experiências (card #187 do core, commits `17081b8` e `7ac4730`).
 *
 * Experiência **não é anúncio**: tem agenda, vaga, participante e presença. O
 * vocabulário abaixo espelha os enums do core (`ExperienceEnums.kt`) e precisa
 * ficar em sincronia com eles - o core inclusive registra isso por escrito no
 * KDoc do `ExperienceType`.
 */

/**
 * O FORMATO da experiência. O assunto é a categoria (compartilhada com o
 * catálogo), não isto.
 */
export type ExperienceType =
  | 'TASTING'
  | 'DINNER'
  | 'CLASS'
  | 'DAY_USE'
  | 'WORKSHOP'
  | 'EXCLUSIVE_MENU'
  | 'VIP'
  | 'GUIDED_TOUR'
  | 'OTHER';

export const EXPERIENCE_TYPE_LABELS: Record<ExperienceType, string> = {
  TASTING: 'Degustação',
  DINNER: 'Jantar temático',
  CLASS: 'Aula experimental',
  DAY_USE: 'Day use',
  WORKSHOP: 'Workshop',
  EXCLUSIVE_MENU: 'Menu exclusivo',
  VIP: 'Experiência VIP',
  GUIDED_TOUR: 'Visita guiada',
  OTHER: 'Outro formato',
};

/**
 * Ciclo de vida e moderação.
 *
 * Sem `EXPIRED`, ao contrário do anúncio: experiência não vence, quem vence é a
 * sessão dela. Uma experiência sem data futura some da vitrine e continua
 * `ACTIVE` para o lojista abrir novas datas.
 */
export type ExperienceStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'ACTIVE'
  | 'PAUSED'
  | 'REJECTED'
  | 'ARCHIVED';

export const EXPERIENCE_STATUS_LABELS: Record<ExperienceStatus, string> = {
  DRAFT: 'Rascunho',
  PENDING_REVIEW: 'Em análise',
  ACTIVE: 'No ar',
  PAUSED: 'Pausada',
  REJECTED: 'Recusada',
  ARCHIVED: 'Encerrada',
};

/* ======================================================================== */
/* Moderação (backoffice, card #352)                                        */
/* ======================================================================== */

/**
 * Uma experiência na fila de moderação do Kardume Interno.
 *
 * Espelha `ExperienceModerationDto` do core. `upcomingSessions` é o campo que
 * muda a resposta que se dá ao lojista: aprovar uma experiência sem data
 * marcada **não** a coloca na vitrine, porque a consulta pública exige data
 * futura.
 */
export interface ExperienceModerationDto {
  id: string;
  merchantId: string;
  merchantName: string;
  title: string;
  slug: string;
  type: ExperienceType;
  status: ExperienceStatus;
  description?: string | null;
  rules?: string | null;
  /** Preço cobrado pela loja, em reais. Zero quando `free`. */
  price: number;
  free: boolean;
  kardumeExclusive: boolean;
  /** Nome do plano mínimo do Kardume Pass, quando a loja exigiu um. */
  requiredPassPlanName?: string | null;
  categoryName?: string | null;
  /** Datas futuras já marcadas. Zero = aprovar não coloca na vitrine. */
  upcomingSessions: number;
  rejectionReason?: string | null;
  moderatedAt?: string | null;
  /** Id do funcionário do backoffice que julgou. Nulo enquanto na fila. */
  moderatedBy?: string | null;
  createdAt: string;
}

/**
 * A decisão da moderação.
 *
 * `rejectionReason` é obrigatório quando `approved` é falso - o core recusa com
 * `EXPERIENCE_REJECTION_REASON_REQUIRED`, e a tela precisa exigir o texto antes
 * de gastar o request: editar uma experiência recusada a devolve para a fila,
 * então sem saber o que corrigir o lojista reenvia igual.
 */
export interface ExperienceModerationRequestDto {
  approved: boolean;
  rejectionReason?: string;
}

/** Copy para os `errorCode`s que o core devolve ao recusar uma moderação. */
export const EXPERIENCE_MODERATION_ERROR_MESSAGES: Record<string, string> = {
  EXPERIENCE_REJECTION_REASON_REQUIRED:
    'Escreva o motivo da recusa - é ele que diz ao lojista o que corrigir.',
  EXPERIENCE_NOT_PENDING_REVIEW:
    'Esta experiência não está mais esperando decisão. Atualize a fila para ver a situação atual.',
};

/* ======================================================================== */
/* Painel do lojista - catálogo, fotos e agenda (cards #347 e #348)         */
/* ======================================================================== */

/**
 * Estado de uma data da agenda.
 *
 * Só dois, e é de propósito: "lotada" **não** é status - é
 * `reservedCount === capacity`, um fato derivado que muda a cada reserva.
 */
export type ExperienceSessionStatus = 'SCHEDULED' | 'CANCELED';

export const EXPERIENCE_SESSION_STATUS_LABELS: Record<ExperienceSessionStatus, string> = {
  SCHEDULED: 'Marcada',
  CANCELED: 'Cancelada',
};

/** Uma experiência na lista do painel do lojista. */
export interface MerchantExperienceDto {
  id: string;
  slug: string;
  title: string;
  type: ExperienceType;
  status: ExperienceStatus;
  price: number;
  free: boolean;
  kardumeExclusive: boolean;
  requiredPassPlanId?: string | null;
  requiredPassPlanName?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  /** A foto de menor `sortOrder`. Não existe campo de capa separado. */
  coverImageUrl?: string | null;
  rejectionReason?: string | null;
  /** Zero é o aviso de "sua experiência sumiu da vitrine". */
  upcomingSessions: number;
  createdAt: string;
}

/** O que a loja envia ao criar ou editar. */
export interface MerchantExperienceRequestDto {
  title: string;
  type: ExperienceType;
  categoryId?: string;
  description?: string;
  rules?: string;
  /** Em reais. Zero = gratuita (`free` é derivado disto no core). */
  price: number;
  kardumeExclusive: boolean;
  /** Tier mínimo do Kardume Pass. Ausente = aberta a todo mundo. */
  requiredPassPlanId?: string;
}

export interface MerchantExperienceImageDto {
  id: string;
  url: string;
  sortOrder: number;
}

/** Detalhe: a experiência, as fotos e a agenda inteira (passado inclusive). */
export interface MerchantExperienceDetailDto {
  experience: MerchantExperienceDto;
  description?: string | null;
  rules?: string | null;
  images: MerchantExperienceImageDto[];
  sessions: MerchantExperienceSessionDto[];
}

/**
 * Uma data da agenda como o lojista a vê.
 *
 * `reservedCount` e `checkedInCount` são separados porque respondem a perguntas
 * diferentes na mesma noite: quantos lugares estão comprometidos, e quantas
 * pessoas já entraram.
 */
export interface MerchantExperienceSessionDto {
  id: string;
  /** Instante com fuso (ISO 8601 com offset). */
  startsAt: string;
  endsAt: string;
  capacity: number;
  reservedCount: number;
  availableSeats: number;
  full: boolean;
  status: ExperienceSessionStatus;
  waitlistCount: number;
  checkedInCount: number;
  /** Presente quando a data nasceu de uma agenda recorrente. */
  recurrenceGroupId?: string | null;
}

/** Uma data avulsa. Instantes com fuso - nunca `LocalDateTime` sem offset. */
export interface MerchantExperienceSessionRequestDto {
  startsAt: string;
  endsAt: string;
  capacity: number;
}

/**
 * Uma agenda que se repete ("toda quinta às 20h até dezembro").
 *
 * Horário e período vão como hora local (`"20:00"`) e data (`"2026-12-31"`)
 * porque é assim que o lojista pensa; a conversão para instante acontece uma
 * vez só, no fuso do negócio, dentro do core.
 */
export interface MerchantExperienceRecurrenceRequestDto {
  daysOfWeek: WeekdayCode[];
  /** `"20:00"`. */
  startsAtTime: string;
  durationMinutes: number;
  /** `"2026-09-01"`. */
  from: string;
  until: string;
  capacity: number;
}

/**
 * O que a criação de uma recorrência devolve.
 *
 * `created` é o que a tela mostra - **não** o número de datas pedidas: o core
 * pula em silêncio as que colidem com o que já existe.
 */
export interface MerchantExperienceRecurrenceResultDto {
  recurrenceGroupId: string;
  created: number;
  sessions: MerchantExperienceSessionDto[];
}

/** Teto de sessões por recorrência (`EXPERIENCE_RECURRENCE_TOO_MANY`). */
export const EXPERIENCE_RECURRENCE_MAX_SESSIONS = 400;

/** Janela máxima de uma recorrência, em dias (`EXPERIENCE_RECURRENCE_TOO_LONG`). */
export const EXPERIENCE_RECURRENCE_MAX_DAYS = 365;

/**
 * Copy para os `errorCode`s do painel do lojista.
 *
 * `EXPERIENCE_CAPACITY_BELOW_RESERVED` fica **fora** da lista de propósito: a
 * mensagem do core traz o piso ("já existem 12 lugares ocupados"), que é
 * exatamente o número que o lojista precisa ler. Sobrescrevê-la aqui trocaria
 * uma mensagem útil por uma genérica.
 */
export const MERCHANT_EXPERIENCE_ERROR_MESSAGES: Record<string, string> = {
  EXPERIENCE_TITLE_REQUIRED: 'Informe o título da experiência.',
  EXPERIENCE_TYPE_REQUIRED: 'Escolha o formato da experiência.',
  EXPERIENCE_STATUS_NOT_ALLOWED:
    'A loja pode pausar, retomar e encerrar. Aprovar e recusar são da moderação do Kardume.',
  EXPERIENCE_HAS_ACTIVE_RESERVATIONS:
    'Há datas futuras com reservas. Cancele essas datas primeiro - quem reservou precisa ser avisado antes de a experiência sumir.',
  EXPERIENCE_SESSION_OVERLAP: 'Já existe uma data desta experiência neste horário.',
  EXPERIENCE_SESSION_CANCELED: 'Esta data já havia sido cancelada. Atualize a agenda.',
  EXPERIENCE_SESSION_INVALID_CAPACITY: 'A data precisa oferecer pelo menos uma vaga.',
  EXPERIENCE_RECURRENCE_EMPTY:
    'Nenhuma data cai nos dias da semana escolhidos dentro do período informado.',
  EXPERIENCE_RECURRENCE_NOTHING_TO_CREATE:
    'Todas as datas geradas já existem na agenda ou estão no passado - nada foi criado.',
  EXPERIENCE_RECURRENCE_INVERTED: 'A data final não pode ser anterior à inicial.',
  EXPERIENCE_RECURRENCE_TOO_LONG: `A agenda é gerada para no máximo ${EXPERIENCE_RECURRENCE_MAX_DAYS} dias de cada vez.`,
};

/* ======================================================================== */
/* Participantes, não comparecimento e avaliações (card #348)               */
/* ======================================================================== */

/**
 * Estado de uma reserva - e, o que importa na tela, **o que cada estado faz
 * com a vaga**:
 *
 * | Estado        | Ocupa vaga? |
 * |---------------|-------------|
 * | `WAITLISTED`  | não         |
 * | `CONFIRMED`   | sim         |
 * | `CHECKED_IN`  | sim         |
 * | `CANCELED`    | **devolve** |
 * | `NO_SHOW`     | **não devolve** |
 *
 * A diferença entre `CANCELED` e `NO_SHOW` é a razão de existirem dois
 * estados, e é o que a tela precisa deixar explícito: usar um pelo outro ou
 * perde a vaga para sempre, ou a devolve quando já não há mais ninguém para
 * ocupá-la.
 */
export type ExperienceReservationStatus =
  | 'WAITLISTED'
  | 'CONFIRMED'
  | 'CHECKED_IN'
  | 'CANCELED'
  | 'NO_SHOW';

export const EXPERIENCE_RESERVATION_STATUS_LABELS: Record<ExperienceReservationStatus, string> = {
  WAITLISTED: 'Fila de espera',
  CONFIRMED: 'Confirmada',
  CHECKED_IN: 'Presente',
  CANCELED: 'Cancelada',
  NO_SHOW: 'Não compareceu',
};

/**
 * Um participante na lista da data.
 *
 * **Sem o token do QR Code, de propósito.** O core não o devolve aqui e a tela
 * não deve pedi-lo: com o token visível, qualquer pessoa da equipe registraria
 * a presença de quem não veio, e o check-in deixaria de provar que alguém
 * esteve na loja. O token só existe para o dono da reserva.
 */
export interface MerchantExperienceParticipantDto {
  reservationId: string;
  customerId: string;
  customerName: string;
  status: ExperienceReservationStatus;
  /** Posição na fila de espera. Só existe em `WAITLISTED`. */
  waitlistPosition?: number | null;
  reservedAt: string;
  checkedInAt?: string | null;
  canceledAt?: string | null;
  noShowAt?: string | null;
}

/**
 * Uma avaliação de experiência, como o lojista a lê e responde.
 *
 * **Não é avaliação da loja.** São notas separadas, telas separadas e médias
 * separadas: `/v1/merchant/reviews` continua sendo a da loja, e somar as duas
 * num indicador só misturaria "gostei do lugar" com "a degustação valeu".
 */
export interface MerchantExperienceReviewDto {
  id: string;
  experienceId: string;
  experienceTitle: string;
  /** 1 a 5. */
  rating: number;
  comment?: string | null;
  customerName: string;
  status: ReviewStatus;
  reply?: string | null;
  repliedAt?: string | null;
  createdAt: string;
}

/** A resposta da loja. A loja responde; nunca altera o texto do cliente. */
export interface MerchantExperienceReviewReplyDto {
  reply: string;
}

/** Copy para as recusas de não comparecimento. */
export const EXPERIENCE_NO_SHOW_ERROR_MESSAGES: Record<string, string> = {
  EXPERIENCE_NO_SHOW_TOO_EARLY:
    'A data ainda não começou. Só depois do horário de início dá para dizer que alguém não veio.',
  EXPERIENCE_NO_SHOW_NOT_ALLOWED:
    'Só uma reserva confirmada pode virar falta. Quem já fez check-in esteve na loja, e presença não se desfaz.',
};

/** Copy para as recusas ao responder uma avaliação. */
export const EXPERIENCE_REVIEW_ERROR_MESSAGES: Record<string, string> = {
  EXPERIENCE_REVIEW_REPLY_REQUIRED: 'Escreva a resposta antes de enviar.',
};
